import type { AmpPageType } from '@/constants';
import { pageOverviewBannerQuery, seoSectionQuery } from '@/fetchers';
import type { ILandingPage } from '@/interfaces';
import { landingPageDataModify, minifyQuery } from '@/utils';

import callContentful from './base.fetchers';

type GetLandingPage = (args: {
  isPreviewMode: boolean;
  ampPageType?: AmpPageType;
  slug?: string;
}) => Promise<ILandingPage | null>;

export const getLandingPage: GetLandingPage = async ({ isPreviewMode, ampPageType, slug }) => {
  const query = /* GraphQL */ `
    {
      landingPageCollection(
        preview: ${isPreviewMode}
        where: {
          ${ampPageType ? `ampPageType: "${ampPageType}"` : ''}
          ${slug ? `slug: "${slug}"` : 'slug_exists: false'}
        }
        limit: 1
      ) {
        items {
          sys { id }
          slug
          title
          breadcrumbsTheme
          onlyLogo
          ampPageType
          seoPageType
          faq
          trackingSolution
          pageOverviewBanner
          pageOverviewBannerHeroImage {
            contentType
            url
            description
          }
          pageOverviewBannerCtaButtons: pageOverviewBannerCtaButtonsCollection(limit: 3) {
            items {
              sys { id }
              primaryButtonText
              secondaryButtonText
              function
              color
              buttonLink
              appStoreUrl
              googlePlayUrl
              email
              openInNewTab
              hideOnLoggedIn
            }
          }
          landingPageSeo
          supportedLocales
          content {
            json
            links {
              assets {
                block {
                  sys {
                    id
                  }
                  __typename
                  contentType
                  url
                  title
                  description
                  height
                  width
                }
              }
            }
          }
          sectionList: sectionListCollection(
            preview: ${isPreviewMode}
            limit: 15
          ) {
            items {
              __typename
              ... on Wrapper {
                title
                subText
                subTextMobile
                backgroundColor
                image {
                  contentType
                  url
                  description
                }
                content {
                  json
                  links {
                    assets {
                      block {
                        sys {
                          id
                        }
                        __typename
                        contentType
                        url
                        title
                        description
                        height
                        width
                      }
                    }
                  }
                }
                entryList: entryListCollection(limit: 20) {
                  items {
                    __typename
                    ... on ContentItem {
                      uiTemplate
                      title
                      subTitle
                      longSubTitle
                      image {
                        contentType
                        url(transform: {
                          width: 560,
                        })
                        description
                      }
                      buttonText
                      buttonLink
                      openInNewTab
                    }
                  }
                }
              }
              ${seoSectionQuery()}
              ... on LeadGenBanner {
                title
                titleMobile
                image {
                  contentType
                  url(transform: {
                    width: 1261,
                  })
                  description
                }
                imageMobile {
                  contentType
                  url(transform: {
                    width: 696,
                  })
                  description
                }
                subCopy
                subCopyMobile
                buttons: buttonsCollection(limit: 3) {
                  items {
                    sys {
                      id
                    }
                    primaryButtonText
                    secondaryButtonText
                    function
                    color
                    email
                    buttonLink
                    appStoreUrl
                    googlePlayUrl
                    buttonEventLabel
                    openInNewTab
                  }
                }
                sectionTheme: theme
                isLinkedInBanner
              }
              ${pageOverviewBannerQuery()}
            }
          }
          seo {
            title
            description
            ogType
            ogUrl
            ogImage {
              contentType
              url
            }
            ogTitle
            ogDescription
            robots
          }
        }
      }
    }
  `;

  const _slug = `/${slug}` || 'homepage';
  const response = await callContentful(
    minifyQuery(query),
    isPreviewMode,
    _slug,
    'landingPage.fetchers.getLandingPage',
  );
  const landingPageData = landingPageDataModify(response.data?.landingPageCollection.items?.filter(Boolean)?.[0]);

  if (!landingPageData) return null;

  return landingPageData;
};
